import { Loading } from '../../components/loading/Loading';
import {
  logout,
  useUser,
  useUserKey,
  remoteSignInSuccess,
  remoteSignInStart,
  remoteSignInFailed,
  REMOTE_SIGN_IN_ERROR,
  REMOTE_SIGN_IN_TIMEOUT
} from '../../features/user/ssoSlice';
import React, { useMemo } from 'react';
import { Redirect, useHistory } from 'react-router';
import { API_PATH } from '../../config';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { LANGUAGE_LOCALES } from '../../features/localization/languages';
import { StorageKey } from '../../features/localStorage/storageKey';
import { getPathFromAuthedRedirectUrl } from '../../utils/authedRedirectUrl';
import { RouteKey } from '../../config/page-key';
import moment from 'moment';
import { useLocation } from 'react-router';
import { useKCConfig } from './ssoHook';
import Keycloak from 'keycloak-js';
const SSO_API_AUTH_URL = `${API_PATH}/auth/sso`;
const KEYCLOAK_INIT_STATUS = {
  LOADING: 0,
  LOADED: 1,
  ERROR: 2
};

export function SSO() {
  const user = useUser();
  const userKey = useUserKey();
  const dispatch = useDispatch();
  const keycloakConfig = useKCConfig();
  const query = new URLSearchParams(useLocation().search)
  const [keycloakInitStatus, setKeycloakInitStatus] = useState(KEYCLOAK_INIT_STATUS.LOADING);

  const [loginError, setLoginError] = useState('');

  const handlers = useMemo(() => {
    return {
      onSSOStart: () => {
        dispatch(remoteSignInStart());
        setKeycloakInitStatus(KEYCLOAK_INIT_STATUS.LOADED);
      },
      onInitError: e => {
        setLoginError(REMOTE_SIGN_IN_ERROR.KC_INIT_ERROR);
        setKeycloakInitStatus(KEYCLOAK_INIT_STATUS.ERROR);
        dispatch(remoteSignInFailed(REMOTE_SIGN_IN_ERROR.KC_INIT_ERROR));
      },
      onAuthLogout: () => {
        //keycloak logout
        let redirectUrl = '/sso';
        if (keycloakConfig.realm) {
          redirectUrl += '?realm=' + keycloakConfig.realm;
        dispatch(logout());
      }
        dispatch(remoteSignInFailed());
        window.location.href = redirectUrl;
      },
      onAuthError: () => {
        dispatch(logout());
        setLoginError(REMOTE_SIGN_IN_ERROR.KC_AUTH_ERROR);
        dispatch(remoteSignInFailed(REMOTE_SIGN_IN_ERROR.KC_AUTH_ERROR));
      },
      onAPIKeyError: (isKeyEmpty = false, authResp = {}) => {
        const hasAttemptsReturned =
          authResp && authResp?.failedAttempts !== undefined && authResp?.maxAttempts !== undefined;

        const err = isKeyEmpty
          ? REMOTE_SIGN_IN_ERROR.API_KEY_EMPTY_ERROR
          : REMOTE_SIGN_IN_ERROR.API_KEY_ERROR;
        dispatch(remoteSignInFailed(err));
        if (window?.keycloak?.logout) {
          let redirectUrl = `${window.location.origin}/error`;

          if (hasAttemptsReturned) {
            const userBlocked = authResp.failedAttempts === authResp.maxAttempts;
            const userCompanyBlocked = authResp.maxAttempts === 0;

            if (userBlocked || userCompanyBlocked) {
              redirectUrl = RouteKey.SSO;

              if (keycloakConfig.realm) {
                redirectUrl += '?realm=' + keycloakConfig.realm;
              }

              redirectUrl = window.location.origin + redirectUrl;
            }
          }

          window.keycloak.logout({ redirectUri: redirectUrl }).catch(e => setLoginError(err));
        } else {
          setLoginError(err);
        }
      },
      onSSOSuccess: authObj => {
        const {role, ...props} = authObj;
        window.localStorage.setItem(StorageKey.USER_ACTION_TIME, moment().format('X'));
        dispatch(remoteSignInSuccess({...props}));
      }
    };
  }, [dispatch, keycloakConfig]);

  const history = useHistory();
  if (user && userKey) {
    return (
      <Redirect
        to={getPathFromAuthedRedirectUrl({
          urlSearch: history.location.search
        })}
      />
    );
  }
  
  if (window.keycloak == null) 
    window.keycloak = new Keycloak({
      ...keycloakConfig,
      url: keycloakConfig.url ?? keycloakConfig['auth-server-url']
    });
  if (query.get('logoutType')) {
    const lang = localStorage.getItem('userPrefsLanguage') || LANGUAGE_LOCALES.EN_AU;

    const url = `${keycloakConfig['url']}realms/${
      keycloakConfig.realm
    }/TN360/Logout?logoutType=${query.get('logoutType')}&loginUrl=${
      window.location.origin
    }/sso&kc_locale=${lang}`;
    return window.location.replace(url);
  } else if (keycloakInitStatus === KEYCLOAK_INIT_STATUS.LOADING && window.keycloak?.didInitialize !== true) {
    ssoAuth({
      keycloakConfig,
      ...handlers,
      logoutType: query.get('logoutType'),
      realm: keycloakConfig.realm
    });
  }
  
  return <PageLoader />;
}

const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      width: '100%'
    }}
  >
    <Loading />
  </div>
);

const ssoAuth = async ({
  keycloakConfig,
  onSSOStart = () => {},
  onInitError = () => {},
  onAuthError = () => {},
  onAuthLogout = () => {},
  onAPIKeyError = () => {},
  onSSOSuccess = () => {},
  logoutType,
  realm
}) => {
  onSSOStart();
  window.keycloak.onAuthLogout = onAuthLogout;
  window.keycloak.onTokenExpired = () => {
    window.keycloak
      .updateToken(30)
      .catch(onAuthError);
  };
  const queryParams = new URLSearchParams(window.location.search);
  const from = queryParams.get('from');

  let ssoObj = {
    flow: 'standard',
    onLoad: 'login-required',
    messageReceiveTimeout: REMOTE_SIGN_IN_TIMEOUT,
    redirectUri: `${window.location.origin}/sso?realm=${realm}` + (from ? `&&page=${from}` : ''),
    checkLoginIframe: false
  };

  return window.keycloak
    .init(ssoObj)
    .then(async authenticated => {
      if (authenticated && window.keycloak.token) {
        return getAPIKeyAuth({
          keycloakAuthToken: window.keycloak.token,
          onAPIKeyError,
          onAPIKeySuccess: authObj =>
            onSSOSuccess({
              ...authObj,
              auth: {
                ...authObj?.auth,
                keycloakConfig
              }
            })
        });
      } else {
        let lang = localStorage.getItem(StorageKey.LOCALE) || LANGUAGE_LOCALES.EN_AU;
        let userRealm = realm;

        if (userRealm === undefined || userRealm === '') {
          userRealm = keycloakConfig.realm;
        }

        if (logoutType) {
          let baseUrl = keycloakConfig['url'];
          let url =
            baseUrl +
            'realms/' +
            userRealm +
            '/TN360/Logout?logoutType=' +
            logoutType +
            '&loginUrl=' +
            window.location.origin+
            '/sso' +
            '&kc_locale=' +
            lang;
          return window.location.replace(url);
        } else {
          const isLoginDisabled = await isKCLoginDisabled(
            window.keycloak.createLoginUrl({ locale: lang })
          );
          return isLoginDisabled ? onAuthError() : window.keycloak.login({ locale: lang });
        }
      }
    })
    .catch(onInitError);
};

const isKCLoginDisabled = async kcLoginUrl =>
  fetch(kcLoginUrl, { credentials: 'include' })
    .then(resp => resp.status === 400)
    .catch(e => false);

const getAPIKeyAuth = ({
  keycloakAuthToken,
  onAPIKeySuccess = authObj => {},
  onAPIKeyError = isKeyEmpty => {},
  retry = 3
}) => {
  return fetch(SSO_API_AUTH_URL, { headers: { Authorization: `Bearer ${keycloakAuthToken}` } })
    .then(async resp => {
      let isKeyEmpty = false;
      const authObj = await resp.json();
      if (resp.status === 200) {
        const userKey = authObj?.keys?.find(k => k.type === 'USER');
        if (userKey) {
          return onAPIKeySuccess({
            ...authObj,
            auth: {
              ...authObj?.auth,
              key: userKey.accessToken
            }
          });
        } else {
          isKeyEmpty = true;
        }
      }
      return retry > 0
        ? getAPIKeyAuth({ keycloakAuthToken, onAPIKeySuccess, onAPIKeyError, retry: retry - 1 })
        : onAPIKeyError(isKeyEmpty, authObj);
    })
    .catch(e => {
      console.error(e);
      return retry > 0
        ? getAPIKeyAuth({ keycloakAuthToken, onAPIKeySuccess, onAPIKeyError, retry: retry - 1 })
        : onAPIKeyError();
    });
};
